import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    professionalType: 'Medico',
    crm: '',
    crmState: '',
    crfa: '',
    crfaRegion: '',
    crea: '',
    creaState: '',
  });
  const [loading, setLoading] = useState(false);
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validações
    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    // Validar campos profissionais obrigatórios
    if (formData.professionalType === 'Medico' && (!formData.crm || !formData.crmState)) {
      toast.error('CRM e Estado são obrigatórios para médicos');
      return;
    }

    if (formData.professionalType === 'Fonoaudiologo' && (!formData.crfa || !formData.crfaRegion)) {
      toast.error('CRFa e Região são obrigatórios para fonoaudiólogos');
      return;
    }

    if (formData.professionalType === 'Engenheiro' && (!formData.crea || !formData.creaState)) {
      toast.error('CREA e Estado são obrigatórios para engenheiros');
      return;
    }

    try {
      setLoading(true);
      await register(formData);
    } catch (error) {
      console.error('Erro ao criar conta:', error);
    } finally {
      setLoading(false);
    }
  };

  const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const crfaRegions = [
    '1ª Região (SP, MS, MT)',
    '2ª Região (MG, ES)',
    '3ª Região (PR, SC, RS)',
    '4ª Região (PE, PB, RN, AL)',
    '5ª Região (BA, SE)',
    '6ª Região (DF, GO, TO, AM, RR, AC, RO, AP, PA)',
    '7ª Região (RJ, MA, PI, CE)',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-600 via-tech-600 to-health-600 flex items-center justify-center px-4 py-8">
      <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-2xl w-full">
        <div className="text-center mb-8">
          <img 
            src="/logo-audfast.png" 
            alt="AudFast - Velocidade e Precisão em Cada Decibel" 
            className="h-20 object-contain mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gradient mb-2">Criar Conta</h1>
          <p className="text-gray-600">Preencha os dados para começar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome Completo */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Nome Completo *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
              placeholder="Dr. João Silva"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              E-mail *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
              placeholder="seu.email@exemplo.com"
              required
            />
          </div>

          {/* Senha */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Senha *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                placeholder="••••••••"
                required
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Senha *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Tipo Profissional */}
          <div>
            <label htmlFor="professionalType" className="block text-sm font-medium text-gray-700 mb-2">
              Tipo Profissional *
            </label>
            <select
              id="professionalType"
              name="professionalType"
              value={formData.professionalType}
              onChange={handleChange}
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
              required
            >
              <option value="Medico">Médico</option>
              <option value="Fonoaudiologo">Fonoaudiólogo</option>
              <option value="Engenheiro">Engenheiro</option>
            </select>
          </div>

          {/* Campos específicos por tipo profissional */}
          {formData.professionalType === 'Medico' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="crm" className="block text-sm font-medium text-gray-700 mb-2">
                  CRM *
                </label>
                <input
                  type="text"
                  id="crm"
                  name="crm"
                  value={formData.crm}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                  placeholder="123456"
                  required
                />
              </div>
              <div>
                <label htmlFor="crmState" className="block text-sm font-medium text-gray-700 mb-2">
                  Estado do CRM *
                </label>
                <select
                  id="crmState"
                  name="crmState"
                  value={formData.crmState}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                  required
                >
                  <option value="">Selecione...</option>
                  {brazilianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {formData.professionalType === 'Fonoaudiologo' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="crfa" className="block text-sm font-medium text-gray-700 mb-2">
                  CRFa *
                </label>
                <input
                  type="text"
                  id="crfa"
                  name="crfa"
                  value={formData.crfa}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                  placeholder="123456"
                  required
                />
              </div>
              <div>
                <label htmlFor="crfaRegion" className="block text-sm font-medium text-gray-700 mb-2">
                  Região do CRFa *
                </label>
                <select
                  id="crfaRegion"
                  name="crfaRegion"
                  value={formData.crfaRegion}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                  required
                >
                  <option value="">Selecione...</option>
                  {crfaRegions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {formData.professionalType === 'Engenheiro' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="crea" className="block text-sm font-medium text-gray-700 mb-2">
                  CREA *
                </label>
                <input
                  type="text"
                  id="crea"
                  name="crea"
                  value={formData.crea}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                  placeholder="123456"
                  required
                />
              </div>
              <div>
                <label htmlFor="creaState" className="block text-sm font-medium text-gray-700 mb-2">
                  Estado do CREA *
                </label>
                <select
                  id="creaState"
                  name="creaState"
                  value={formData.creaState}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent disabled:opacity-50"
                  required
                >
                  <option value="">Selecione...</option>
                  {brazilianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Botão de Submit */}
          <button 
            type="submit"
            disabled={loading}
            className="btn-primary w-full text-center disabled:opacity-50 disabled:cursor-not-allowed mt-6"
          >
            {loading ? 'Criando conta...' : 'Criar Conta'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-600 mt-6">
          Já tem uma conta?{' '}
          <Link to="/login" className="text-medical-600 hover:underline font-semibold">
            Fazer login
          </Link>
        </p>

        <div className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <Link to="/" className="hover:text-medical-600 transition-colors">
            ← Voltar para Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
